// Локальные tombstones (deleted_at IS NOT NULL) для offline trash.
// Streams фильтруют deleted_at IS NULL — после sync tombstones исчезают с клиента,
// пока не загружен dedicated trash stream. До sync / offline delete — видны здесь.
// Offline-first UX plan фаза 4.
//
// ─────────────────────────────────────────────────────────────────────────────
// Окно 30 дней здесь ОБЯЗАТЕЛЬНО — как и на сервере
// ─────────────────────────────────────────────────────────────────────────────
// «Очистить корзину» не стирает строки сразу: она СОСТАРИВАЕТ `deleted_at` за
// пределы TTL, а физически удаляет ночная чистка (одна точка стирания отвечает
// за S3, дочерние таблицы и порядок — см. `trash/mutations/empty-trash.ts`).
// Серверный список это учитывает (`withinTrashWindow`), а локальная половина —
// нет, и склейка возвращала состаренные строки обратно на экран: тост говорил
// «Корзина очищена», а элементы оставались на месте (жалоба владельца
// 2026-08-17). Строка вне окна для человека уже удалена — показывать её нельзя.

import type { TrashEntityType, TrashGroup } from "@studyhub/shared/contracts/trash";
import { useMemo } from "react";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

interface TombstoneRow {
	id: string;
	title: string;
	deleted_at: string;
	entity_type: TrashEntityType;
}

/** TTL корзины. Зеркалит серверный `TRASH_TTL_DAYS` (`trash/_internal/constants`). */
const TRASH_TTL_DAYS = 30;

function purgeAtFrom(deletedAt: string): string {
	const d = new Date(deletedAt);
	d.setDate(d.getDate() + TRASH_TTL_DAYS);
	return d.toISOString();
}

/** UNION tombstones по основным soft-delete таблицам текущего user. */
export function useLocalTrashQuery(userId: string | undefined, entityType?: TrashEntityType) {
	const sql = useMemo(() => {
		const parts: string[] = [
			`SELECT id, name AS title, deleted_at, 'material' AS entity_type FROM materials
			  WHERE user_id = ? AND deleted_at IS NOT NULL AND deleted_at > ?`,
			`SELECT id, title, deleted_at, 'note' AS entity_type FROM notes
			  WHERE user_id = ? AND deleted_at IS NOT NULL AND deleted_at > ?`,
			`SELECT id, name AS title, deleted_at, 'test' AS entity_type FROM tests
			  WHERE user_id = ? AND deleted_at IS NOT NULL AND deleted_at > ?`,
			`SELECT id, name AS title, deleted_at, 'flashcard_set' AS entity_type FROM flashcard_sets
			  WHERE user_id = ? AND deleted_at IS NOT NULL AND deleted_at > ?`,
			`SELECT id, name AS title, deleted_at, 'collection' AS entity_type FROM folders
			  WHERE user_id = ? AND deleted_at IS NOT NULL AND deleted_at > ?`,
		];
		return `${parts.join(" UNION ALL ")} ORDER BY deleted_at DESC LIMIT 100`;
	}, []);

	const params = useMemo(() => {
		if (!userId) return [];
		// Граница окна считается на КАЖДЫЙ пересчёт, а не один раз при монтировании:
		// вкладку держат открытой часами, и застывшая граница со временем начала бы
		// врать в другую сторону.
		const windowStart = new Date(Date.now() - TRASH_TTL_DAYS * 86_400_000).toISOString();
		return [
			userId,
			windowStart,
			userId,
			windowStart,
			userId,
			windowStart,
			userId,
			windowStart,
			userId,
			windowStart,
		];
	}, [userId]);

	const query = usePowerSyncQuery<TombstoneRow>(
		userId
			? sql
			: `SELECT id, name AS title, deleted_at, 'material' AS entity_type FROM materials WHERE 0`,
		params,
	);

	const groups = useMemo((): TrashGroup[] => {
		const rows = query.data ?? [];
		return rows
			.filter((r) => !entityType || r.entity_type === entityType)
			.map((r) => ({
				entityType: r.entity_type,
				id: r.id,
				title: r.title || "—",
				deletedAt: r.deleted_at,
				cascadeBatchId: null,
				children: [],
				purgeAt: purgeAtFrom(r.deleted_at),
			}));
	}, [query.data, entityType]);

	return { ...query, groups };
}

/**
 * Тип корзины → локальная таблица SQLite, из которой офлайн-режим может её
 * прочитать. `null` — тип в офлайне не показывается.
 *
 * Карта ИСЧЕРПЫВАЮЩАЯ (`Record`, не `Partial<Record>`) — Ш5. Раньше в ней
 * было пять строк из восемнадцати, и отличить «тип сознательно не в офлайне»
 * от «про тип забыли» было невозможно: оба случая давали один и тот же
 * `undefined`. Теперь каждый тип назван, а `null` стоит с причиной; новый
 * trash-тип без решения не скомпилируется.
 *
 * Причина `null` почти всегда одна: таблица-владелец не синкается PowerSync
 * (см. `SYNC_OPT_OUT` в coverage-сканере) либо синкается read-only без
 * `deleted_at`-фильтра, пригодного для локального списка корзины.
 */
const TABLE_BY_ENTITY: Record<TrashEntityType, string | null> = {
	material: "materials",
	note: "notes",
	test: "tests",
	flashcard_set: "flashcard_sets",
	collection: "folders",
	// Синкаются, но в локальной корзине не показываются: это дочерние строки,
	// пользователь восстанавливает их вместе с родителем (набор / тест).
	flashcard: null,
	recording_manual_note: null,
	// Синкаются, но корзина по ним ведётся сервером (каскад по сессии).
	recording: null,
	tutor_session: null,
	voice_session: null,
	study_plan: null,
	calendar_event: null,
	glossary_term: null,
	audio_recap: null,
	tutor_skill: null,
	tutor_scenario: null,
	// Не синкаются вовсе — эссе и визуальные разборы читаются через tRPC.
	essay: null,
	visual_analysis: null,
};

export function trashTableForEntity(entityType: TrashEntityType): string | null {
	return TABLE_BY_ENTITY[entityType];
}
